import type { Issue } from "../state/validation";
import type { TFunction } from "i18next";
import { isNil } from "lodash-es";
import type { TranslationKey } from "../state/i18n";
import type { ArrayPathItem } from "valibot";

export type ValidationError = {
  property?: string;
  message: string;
};

export type NestedValidationError = ValidationError & {
  index: number;
};

export const toValidationErrors = (
  issues: Issue[],
  propertyKeys: Record<string, TranslationKey>,
  t: TFunction,
): ValidationError[] =>
  issues.map((issue) => ({
    property: getTranslatedPath(issue, propertyKeys, t),
    message: issue.message,
  }));

const getTranslatedPath = (
  error: Issue,
  propertyKeys: Record<string, TranslationKey>,
  t: TFunction,
): string | undefined => {
  const firstPath = error.path?.[0];
  const path = !isNil(firstPath) && "key" in firstPath ? (firstPath.key as string | undefined) : undefined;
  if (isNil(path)) {
    return undefined;
  }

  const propertyKey = propertyKeys[path];

  return !isNil(propertyKey) ? t(propertyKey) : path;
};

export const getErrorForIndex = (index: number, errors?: Issue[]): Issue | undefined =>
  errors?.find(
    (error) =>
      error.path?.filter((path) => path.type === "array").find((path) => (path as ArrayPathItem).key === index) !==
      undefined,
  );

export const getErrorMessageForKey = (key: string, error?: Issue): string | undefined => {
  if (isNil(error?.path)) {
    return;
  }

  return error.path.filter((path) => path.type === "object").some((path) => path.key === key)
    ? error.message
    : undefined;
};

export const getErrorMessagesForKey = (key: string, errors?: Issue[]): NestedValidationError[] => {
  if (isNil(errors)) {
    return [];
  }

  return errors
    .map((error) => {
      const objectPathItems = error.path?.filter((path) => path.type === "object");
      const index = error.path?.find((path) => path.type === "array")?.key ?? -1;
      const matchingKeyItem = error.path?.some((path) => path.type === "object" && path.key === key) ?? false;
      const lastObjectPathItem = objectPathItems?.[objectPathItems.length - 1];

      if (matchingKeyItem && !isNil(lastObjectPathItem) && error.message) {
        const finalKey = lastObjectPathItem.key as string;
        return { index, property: finalKey, message: error.message };
      }

      return undefined;
    })
    .filter((message) => !isNil(message));
};
